import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { STATES } from "./states";

const KEY = "afhh:caller-state";
type Ctx = { state: string | undefined; setState: (s: string | undefined) => void };
const CallerCtx = createContext<Ctx>({ state: undefined, setState: () => {} });

function read(): string | undefined {
  try {
    const v = sessionStorage.getItem(KEY);
    return v && STATES.some((s) => s.code === v) ? v : undefined;
  } catch { return undefined; }
}

/** The caller's state for this browser tab. It preselects search filters, problem pages and the program panel. */
export function CallerStateProvider({ children }: { children: ReactNode }) {
  const [state, set] = useState<string | undefined>(read);
  const setState = useCallback((s: string | undefined) => {
    set(s);
    try { if (s) sessionStorage.setItem(KEY, s); else sessionStorage.removeItem(KEY); }
    catch { /* storage unavailable: keep it in memory only */ }
  }, []);
  // A session that ends (expired, or signed out elsewhere) ends the call too: the next sign-in in this tab starts
  // with no caller's state, as after the Sign out button.
  useEffect(() => {
    const off = () => setState(undefined);
    window.addEventListener("afhh:signed-out", off);
    return () => window.removeEventListener("afhh:signed-out", off);
  }, [setState]);
  return <CallerCtx.Provider value={{ state, setState }}>{children}</CallerCtx.Provider>;
}

export const useCallerState = (): Ctx => useContext(CallerCtx);

export function CallerStateSelect() {
  const { state, setState } = useCallerState();
  return (
    <div className="flex items-center gap-2">
      <label htmlFor="caller-state" className="whitespace-nowrap text-xs font-semibold text-slate-500">Caller's state</label>
      <select id="caller-state" className="input w-44" value={state ?? ""} onChange={(e) => setState(e.target.value || undefined)}>
        <option value="">Not set</option>
        {STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
      </select>
    </div>
  );
}
